/**
 * ============================================================================
 * Weight Reconciliation — Validación cruzada de Pesos
 * ============================================================================
 *
 * Compara el peso declarado a NIVEL DE CABECERA (registro_documentos) contra
 * la suma del peso de los ítems (base_inventario) para detectar desajustes
 * de captura/OCR que romperían el prorrateo logístico (GIGO).
 *
 * - Cabecera: valor máximo de `peso_bruto_kg` entre docs operativos (Factura, BL, Packing)
 *   con peso > 0. Si el embarque tiene un override (`embarque.peso_bruto_kg`),
 *   ese valor manda (es la fuente autoritativa logística).
 * - Items: suma de `peso_bruto_kg` por item COGS.
 *
 * Tolerancia aprobada: 2%.
 * ============================================================================
 */

export const WEIGHT_RECONCILIATION_TOLERANCE_PCT = 2;

export interface WeightReconciliationInput {
  /** Override autoritativo de peso a nivel embarque (kg). 0/null = no override. */
  embarqueOverrideKg?: number | null;
  /** Documentos del embarque con tipo + peso_bruto_kg. */
  documents: Array<{
    id?: string | null;
    no_documento?: string | null;
    tipo_documento?: string | null;
    peso_bruto_kg?: number | string | null;
  }>;
  /** Items COGS del embarque con peso_bruto_kg. */
  items: Array<{ peso_bruto_kg?: number | string | null }>;
}

/** Desglose por documento que aporta peso a la cabecera. */
export interface WeightDocBreakdown {
  id: string;
  nombre: string;
  tipo: string;
  peso: number;
}

export interface WeightReconciliationResult {
  hasMismatch: boolean;
  /** Peso autoritativo (override > suma de cabeceras). */
  headerKg: number;
  /** Suma del peso de los ítems COGS. */
  itemsKg: number;
  /** Diferencia absoluta en kg (header - items). */
  deltaKg: number;
  /** Diferencia porcentual respecto al header. */
  deltaPct: number;
  source: "override" | "documents" | "none";
  /**
   * Documentos operativos que aportan peso > 0 a la cabecera.
   * Vacío cuando `source === "override"` (peso manual, no derivado de docs).
   */
  docsCalculados: WeightDocBreakdown[];
}

const OPERATIONAL_DOC_TYPES = new Set([
  "factura internacional",
  "factura de compras",
  "bl",
  "b/l",
  "bill of lading",
  "packing list",
  "lista de empaque",
]);

function toNum(v: unknown): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

export function computeWeightReconciliation(
  input: WeightReconciliationInput,
): WeightReconciliationResult {
  const override = toNum(input.embarqueOverrideKg);

  // Documentos operativos con peso > 0 (la unidad de desglose).
  const operationalDocs = input.documents.filter((d) => {
    const t = (d.tipo_documento ?? "").toString().trim().toLowerCase();
    return OPERATIONAL_DOC_TYPES.has(t) && toNum(d.peso_bruto_kg) > 0;
  });

  const docHeaderSum = operationalDocs.length > 0
    ? Math.max(...operationalDocs.map((d) => toNum(d.peso_bruto_kg)))
    : 0;

  const headerKg = override > 0 ? override : docHeaderSum;
  const source: WeightReconciliationResult["source"] =
    override > 0 ? "override" : docHeaderSum > 0 ? "documents" : "none";

  // Override manual → no hay desglose por documento. Si es "documents",
  // listamos todos los aportantes (Opción A) ordenados por peso DESC para
  // que el usuario identifique de inmediato el doc con mayor impacto.
  const docsCalculados: WeightDocBreakdown[] =
    source === "documents"
      ? operationalDocs
          .map((d) => ({
            id: (d.id ?? "").toString(),
            nombre: (d.no_documento ?? "—").toString(),
            tipo: (d.tipo_documento ?? "").toString(),
            peso: toNum(d.peso_bruto_kg),
          }))
          .filter((d) => d.id !== "")
          .sort((a, b) => b.peso - a.peso)
      : [];

  const itemsKg = input.items.reduce((acc, it) => acc + toNum(it.peso_bruto_kg), 0);

  if (headerKg <= 0 || itemsKg <= 0) {
    return {
      hasMismatch: false,
      headerKg,
      itemsKg,
      deltaKg: headerKg - itemsKg,
      deltaPct: 0,
      source,
      docsCalculados,
    };
  }

  const deltaKg = headerKg - itemsKg;
  const deltaPct = (Math.abs(deltaKg) / headerKg) * 100;

  return {
    hasMismatch: deltaPct > WEIGHT_RECONCILIATION_TOLERANCE_PCT,
    headerKg,
    itemsKg,
    deltaKg,
    deltaPct,
    source,
    docsCalculados,
  };
}
